import { useState, type PropsWithChildren } from "react";
import { useWindowDimensions, View } from "@-/com.npmjs.react-native";
import { useIsNavigationFocused } from "@-/dev.improve.haniverse.contexts.primary-controls-strip.opaque-chrome";

/**
 * Retains a virtual row's measured geometry while its expensive contents are off screen.
 * Keep this shell keyed by row identity when a virtualizer recycles its containers.
 */
export function NavigationFocusedRowContent({
  children,
  testID,
}: PropsWithChildren<{ testID?: string }>) {
  const isFocused = useIsNavigationFocused();
  const { fontScale, width: windowWidth } = useWindowDimensions();
  const [measurement, setMeasurement] = useState<{
    fontScale: number;
    height: number;
    width: number;
    windowWidth: number;
  } | null>(null);
  const hasCurrentMeasurement =
    measurement !== null &&
    measurement.fontScale === fontScale &&
    measurement.windowWidth === windowWidth;
  // Never guess an unmeasured row's height. A newly recycled row or changed viewport measures
  // its real contents first. Focus always restores contents in the same render, without a timer.
  const showContents = isFocused || !hasCurrentMeasurement;

  return (
    <View
      onLayout={({ nativeEvent: { layout } }) => {
        if (showContents) {
          if (layout.height > 0 && layout.width > 0) {
            setMeasurement((previous) =>
              previous?.height === layout.height &&
              previous.width === layout.width &&
              previous.windowWidth === windowWidth &&
              previous.fontScale === fontScale
                ? previous
                : { fontScale, height: layout.height, width: layout.width, windowWidth },
            );
          }
        } else if (measurement !== null && layout.width > 0 && layout.width !== measurement.width) {
          // A containing pane can resize without changing the window (for example a sidebar).
          // Measure its new natural height before returning to the lightweight hidden shell.
          setMeasurement(null);
        }
      }}
      style={showContents ? undefined : { height: measurement?.height }}
      testID={testID}
    >
      {showContents ? children : null}
    </View>
  );
}
